import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { login } from '../actions/auth';
import { register } from '../actions/register';
import { acceptInvite, resetPassword } from '../actions/passwordReset';
import { validateToken } from '../helpers/authHelpers';
import { clearLicenseNavCache } from '../helpers/licenseNavCache';

// Rehydrate state if browser refresh happens and token is still valid
export const rehydrate = createAsyncThunk('auth/rehydrate', async(_, { rejectWithValue }) => {
    const token = localStorage.getItem('CloudForge-Token');

    if(!token) {
        return rejectWithValue('No token found');
    }

    const user = await validateToken(token);
    
    if(!user) {
        return rejectWithValue('Invalid token');
    }

    return { user, token };
});

const authSlice = createSlice({
    name: 'auth',
    initialState: {
        user: null,
        isLoading: false,
        isLoggedIn: false,
        errorMessage: null,
    },
    reducers: {
        logout: (state) => {
            state.user = null;
            state.isLoggedIn = false;
            state.hasCheckedRehydrate = false;
            localStorage.removeItem('CloudForge-Token');
            clearLicenseNavCache();
        },
        setErrorMessage: (state, action) => {
            state.errorMessage = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
        .addCase(login.pending, (state) => {
            state.isLoading = true;
            state.errorMessage = null;
        })
        .addCase(login.fulfilled, (state, action) => {
            state.isLoading = false;
            state.user = action.payload.data;
            state.isLoggedIn = true;
            localStorage.setItem('CloudForge-Token', action.payload.token);
        })
        .addCase(login.rejected, (state, action) => {
            state.isLoading = false;
            state.errorMessage = action.payload;
        })
        .addCase(register.pending, (state) => {
            state.isLoading = true;
            state.errorMessage = null;
        })
        .addCase(register.fulfilled, (state, action) => {
            state.isLoading = false;
            state.user = action.payload.data;
            state.isLoggedIn = true;
            localStorage.setItem('CloudForge-Token', action.payload.token);
        })
        .addCase(register.rejected, (state, action) => {
            state.isLoading = false;
            state.errorMessage = action.payload;
        })
        // Set-password flows log the user in exactly like login/register.
        .addCase(resetPassword.pending, (state) => {
            state.isLoading = true;
            state.errorMessage = null;
        })
        .addCase(resetPassword.fulfilled, (state, action) => {
            state.isLoading = false;
            state.user = action.payload.data;
            state.isLoggedIn = true;
            localStorage.setItem('CloudForge-Token', action.payload.token);
        })
        .addCase(resetPassword.rejected, (state, action) => {
            state.isLoading = false;
            state.errorMessage = action.payload;
        })
        .addCase(acceptInvite.pending, (state) => {
            state.isLoading = true;
            state.errorMessage = null;
        })
        .addCase(acceptInvite.fulfilled, (state, action) => {
            state.isLoading = false;
            state.user = action.payload.data;
            state.isLoggedIn = true;
            localStorage.setItem('CloudForge-Token', action.payload.token);
        })
        .addCase(acceptInvite.rejected, (state, action) => {
            state.isLoading = false;
            state.errorMessage = action.payload;
        })
        .addCase(rehydrate.pending, (state) => {
            state.isLoading = true;
        })
        .addCase(rehydrate.fulfilled, (state, action) => {
            state.isLoading = false;
            state.hasCheckedRehydrate = true;
            state.user = action.payload.user;
            state.isLoggedIn = true;
        })
        .addCase(rehydrate.rejected, (state) => {
            state.isLoading = false;
            state.hasCheckedRehydrate = true;
            localStorage.removeItem('CloudForge-Token');
        });
    },
});
export const { logout, setErrorMessage } = authSlice.actions;

export default authSlice.reducer;